import './ProgramDetail.scss'

import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { includes, isEmpty, values, omit } from 'lodash'

import WatchLink from '../../components/WatchLink'
import PeopleInfo from '../../components/PeopleInfo'
import Translate from '../../components/Translate'
import FavoriteButton from '../../components/FavoriteButton'
import Modal from '../../components/Modal'
import LinkWithLocale from '../../components/LinkWithLocale'
import SearchLink from '../searchResult/SearchLink'
import EpisodeDetailInfo from './EpisodeDetailInfo'

import useRouter from '../../hooks/useRouter'

import { DeviceType } from '../../utils/responsive'
import { getI18nContents, toLocaleUrl } from '../../utils/locale'
import { getSigninUrl } from '../../utils/queryString'
import { safeMapLoop } from '../../utils/misc'
import { createCategoryData } from '../../utils/metaUtils'

import { ProgramDetailFragment } from '../../types/generated'

import useDeviceTypeContext from '../../hooks/useDeviceTypeContext'

import { ReleaseStatus } from '../../constants/enums'

const isPossiblePlaying = (releaseStatus: ReleaseStatus | undefined) => {
  if (!releaseStatus) {
    return false
  }
  return includes(values(omit(ReleaseStatus, 'COMINGSOON')), releaseStatus)
}

interface Props {
  program: ProgramDetailFragment
}

const ProgramDetail = ({ program }: Props) => {
  const [isShowingModal, setIsShowingModal] = useState(false)
  const { t } = useTranslation()
  const { history } = useRouter()
  const currentDeviceType = useDeviceTypeContext()

  const i18nContents = getI18nContents(program)
  const getContentStyle = useCallback(() => {
    return {
      backgroundImage: includes([DeviceType.LG, DeviceType.XL, DeviceType.MX], currentDeviceType)
        ? `linear-gradient(180deg, rgba(17,18,18,0) 0%, #111212 100%),
          linear-gradient(270deg, rgba(17,18,18,0.4) -20%, #111212 100%), url(${i18nContents.images.poster})`
        : 'none',
    }
  }, [currentDeviceType, i18nContents.images.poster])

  return (
    <div className="ProgramDetail">
      {isShowingModal && (
        <Modal
          confirmTitle={t('signin.header')}
          onConfirm={() => {
            history.push(toLocaleUrl(getSigninUrl()))
          }}
          onDisableModal={() => setIsShowingModal(false)}
        >
          <p>{t('programDetail.modalContent')}</p>
        </Modal>
      )}
      <div
        className="ProgramDetail__cover"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(17, 18, 18, 0) 0%, rgba(17, 18, 18, 0.7) 50%, #111212 99%), url(${i18nContents.images.poster})`,
        }}
      />
      <div className="ProgramDetail__content" style={getContentStyle()}>
        <div className="ProgramDetail__contentWithImage">
          <div className="ProgramDetail__textContents">
            <div className="ProgramDetail__titleAndMeta">
              <div className="ProgramDetail__categories">
                {program.categories.map((category, index) => (
                  <LinkWithLocale key={category.slug} to={`/category/${category.slug}`}>
                    <Translate target={category} fieldKey="title" />
                    {program.categories.length - 1 > index && ', '}
                  </LinkWithLocale>
                ))}
              </div>
              <h2>{i18nContents.title}</h2>
              <div className="ProgramDetail__subTitle">{i18nContents.secondaryTitle}</div>
            </div>
            <div className="ProgramDetail__buttons">
              {isPossiblePlaying(program.releaseStatus as ReleaseStatus) && <WatchLink programSlug={program.slug} />}
              <FavoriteButton
                programId={program.id}
                programSlug={program.slug}
                onShowingModal={() => setIsShowingModal(true)}
              />
            </div>
            <EpisodeDetailInfo
              episodeTotal={program.meta?.episodeTotal}
              releaseStart={program.releaseStart}
              score={program.meta?.score}
            />
            <div className="ProgramDetail__releaseInfo">
              {program.produceYear && (
                <LinkWithLocale to={`/search?produce_year=${program.produceYear}`}>
                  {program.produceYear}
                </LinkWithLocale>
              )}
              {!(isEmpty(program.produceYear) && isEmpty(program.meta?.produceCountries)) && (
                <span className="ProgramDetail__divider" />
              )}
              <SearchLink
                links={safeMapLoop(program.meta?.produceCountries, (nation: string) => ({
                  id: nation,
                  title: t(nation),
                  to: `/search?produce_country=${nation}`,
                }))}
              />
              {!(isEmpty(program.meta?.produceCountries) && isEmpty(program.genres)) && (
                <span className="ProgramDetail__divider" />
              )}
              <SearchLink links={createCategoryData(program.genres, true)} />
            </div>
            <div className="ProgramDetail__meta">
              <PeopleInfo peoples={program.people} />
            </div>
          </div>
          <div className="ProgramDetail__image">
            <img src={i18nContents.images.poster} alt={`${i18nContents.title} poster`} />
          </div>
        </div>
        <div className="ProgramDetail__synopsis">{i18nContents.synopsis}</div>
      </div>
    </div>
  )
}

export default ProgramDetail
